import React, { useState, useEffect } from 'react';
import { systemAdminQuestionAPI } from '../../../services/api/systemAdminQuestionAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye,
  BarChart3,
  Move,
  Globe,
  Settings,
  Activity,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

const QuestionManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // Add missing filterType state
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'
  const [showAnalytics, setShowAnalytics] = useState(false);

  const questionTypes = [
    { value: 'audio_video', label: 'Audio + Video' },
    { value: 'audio_only', label: 'Audio Only' },
    { value: 'video_only', label: 'Video Only' }
  ];

  useEffect(() => {
    loadQuestions();
    loadAnalytics();
  }, []);

  // Add Esc key handlers for modals
  useEscKeyHandler(showModal, () => setShowModal(false));
  useEscKeyHandler(showAnalytics, () => setShowAnalytics(false));

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await systemAdminQuestionAPI.getQuestions();
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Failed to load questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await systemAdminQuestionAPI.getQuestionAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleCreateQuestion = () => {
    setSelectedQuestion(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleViewQuestion = (question) => {
    setSelectedQuestion(question);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditQuestion = (question) => {
    setSelectedQuestion(question);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        await systemAdminQuestionAPI.deleteQuestion(questionId);
        toast.success('Question deleted successfully');
        loadQuestions();
        loadAnalytics();
      } catch (error) {
        toast.error('Failed to delete question');
      }
    }
  };

  const QuestionModal = () => {
    const [formData, setFormData] = useState({
      question_text: '',
      instruction_text: '',
      question_type: 'audio_video',
      recording_duration: 30,
      is_active: true
    });

    useEffect(() => {
      if (selectedQuestion && (modalMode === 'edit' || modalMode === 'view')) {
        setFormData({
          question_text: selectedQuestion.question_text || '',
          instruction_text: selectedQuestion.instruction_text || '',
          question_type: selectedQuestion.question_type || 'audio_video',
          recording_duration: selectedQuestion.recording_duration || 30,
          is_active: selectedQuestion.is_active ?? true
        });
      }
    }, [selectedQuestion, modalMode]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (modalMode === 'create') {
          await systemAdminQuestionAPI.createQuestion(formData);
          toast.success('Question created successfully');
        } else if (modalMode === 'edit') {
          await systemAdminQuestionAPI.updateQuestion(selectedQuestion.id, formData);
          toast.success('Question updated successfully');
        }
        setShowModal(false);
        loadQuestions();
        loadAnalytics();
      } catch (error) {
        toast.error(`Failed to ${modalMode} question`);
      }
    };

    const isReadOnly = modalMode === 'view';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Create New Global Question' : 
                 modalMode === 'edit' ? 'Edit Global Question' : 'Question Details'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Text *
              </label>
              <textarea
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows={3}
                required
                readOnly={isReadOnly}
                placeholder="Enter the question that patients will see..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instruction Text
              </label>
              <textarea
                value={formData.instruction_text}
                onChange={(e) => setFormData({ ...formData, instruction_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows={2}
                readOnly={isReadOnly}
                placeholder="Optional instructions for patients..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type *
                </label>
                <select
                  value={formData.question_type}
                  onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  disabled={isReadOnly}
                  required
                >
                  {questionTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recording Duration (seconds) *
                </label>
                <input
                  type="number"
                  value={formData.recording_duration}
                  onChange={(e) => setFormData({ ...formData, recording_duration: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  min="5"
                  max="300"
                  readOnly={isReadOnly}
                  required
                />
              </div>
            </div>

            {modalMode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Active (visible to patients)
                </label>
              </div>
            )}

            {modalMode === 'view' && selectedQuestion && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Question Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">Global Question</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className={`ml-2 font-medium ${selectedQuestion.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedQuestion.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Order:</span>
                    <span className="ml-2 font-medium">{selectedQuestion.question_order}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Created:</span>
                    <span className="ml-2 font-medium">
                      {new Date(selectedQuestion.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#0B1C2C]"
                >
                  {modalMode === 'create' ? 'Create Question' : 'Update Question'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  const AnalyticsModal = () => {
    if (!analytics) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Question Analytics</h2>
              <button
                onClick={() => setShowAnalytics(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                <div className="flex items-center">
                  <Globe className="h-8 w-8 text-[#207CE5] mr-3" />
                  <div>
                    <p className="text-sm text-[#207CE5]">Total Questions</p>
                    <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.total_questions}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="h-8 w-8 text-[#008080] mr-3" />
                  <div>
                    <p className="text-sm text-[#008080]">Global Questions</p>
                    <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.global_questions}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                <div className="flex items-center">
                  <Settings className="h-8 w-8 text-[#00BCD4] mr-3" />
                  <div>
                    <p className="text-sm text-[#00BCD4]">Tenant-Specific</p>
                    <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.tenant_specific_questions}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Question Usage</h3>
                <div className="space-y-2">
                  {analytics.question_usage?.slice(0, 10).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.question_text}</p>
                        <p className="text-xs text-gray-500">
                          {item.is_global ? 'Global' : 'Tenant-specific'} • {item.response_count} responses
                        </p>
                      </div>
                      <div className="text-sm font-medium text-gray-600">
                        {item.response_count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Most Used Questions</h3>
                <div className="space-y-2">
                  {analytics.most_used_questions?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.question_text}</p>
                        <p className="text-xs text-gray-500">{item.usage_count} total responses</p>
                      </div>
                      <div className="text-sm font-medium text-[#207CE5]">
                        #{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredQuestions = questions.filter(question => {
    // Always filter out inactive questions first
    if (!question.is_active) {
      return false;
    }
    
    const matchesSearch = question.question_text?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'global' && question.is_global) ||
                         (filterType === 'tenant' && !question.is_global);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Global Question Management</h1>
          <p className="text-gray-600">Manage global questions that appear for all tenants</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAnalytics(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#3D3D3D] text-white rounded-lg hover:bg-[#0B1C2C] transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            <span>Analytics</span>
          </button>
          <button
            onClick={handleCreateQuestion}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Global Question</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            <option value="all">All Questions</option>
            <option value="global">Global Questions</option>
            <option value="tenant">Tenant Questions</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
                <Globe className="h-6 w-6 text-[#207CE5]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Questions</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_questions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
                <CheckCircle className="h-6 w-6 text-[#008080]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Global Questions</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.global_questions}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
                <Activity className="h-6 w-6 text-[#00BCD4]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Responses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics.question_usage?.reduce((sum, q) => sum + q.response_count, 0) || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Question
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuestions.map((question) => (
                <tr key={question.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-[#207CE5] mr-2 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {question.question_text.length > 60 
                            ? question.question_text.substring(0, 60) + '...'
                            : question.question_text}
                        </div>
                        {question.instruction_text && (
                          <div className="text-sm text-gray-500">
                            {question.instruction_text.length > 50
                              ? question.instruction_text.substring(0, 50) + '...'
                              : question.instruction_text}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#207CE5]">
                      {question.question_type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-1" />
                      {question.recording_duration}s
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {question.is_active ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={`ml-2 text-sm ${
                        question.is_active ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {question.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    #{question.question_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewQuestion(question)}
                        className="text-gray-400 hover:text-gray-600"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditQuestion(question)}
                        className="text-gray-400 hover:text-[#207CE5]"
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredQuestions.length === 0 && (
        <div className="text-center py-12">
          <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No questions found</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first global question'}
          </p>
        </div>
      )}

      {/* Modals */}
      {showModal && <QuestionModal />}
      {showAnalytics && <AnalyticsModal />}
    </div>
  );
};

export default QuestionManagement;